import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useDepartments } from '@/common/useDepartments';
import type { ExpressNewOrdersMultiQueryQuery } from '@/generated/graphql/graphql';
import { useSubscribeToExpressNewMulti } from '../query/orders.hooks';
import { groupOrdersByDepartment } from '../utils/ordersBadgeHelpers';

type ExpressOrder = ExpressNewOrdersMultiQueryQuery['order'][number];

interface ExpressOrdersContextValue {
  orders: ExpressOrder[];
  isLoading: boolean;
  ordersByDepartment: Map<number, ExpressOrder[]>;
  departmentCounters: Map<number, { name: string; count: number }>;
}

const ExpressOrdersContext = createContext<ExpressOrdersContextValue | undefined>(undefined);

export const ExpressOrdersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { allDepartmentIds } = useDepartments();
  const { data, isLoading } = useSubscribeToExpressNewMulti({ departmentIds: allDepartmentIds });

  const orders = useMemo(() => data ?? [], [data]);

  const ordersByDepartment = useMemo(() => {
    return orders.reduce<Map<number, ExpressOrder[]>>((acc, order) => {
      const departmentId = order.departmentId;
      if (departmentId === null || departmentId === undefined) {
        return acc;
      }
      const bucket = acc.get(departmentId);
      if (bucket) {
        bucket.push(order);
      } else {
        acc.set(departmentId, [order]);
      }
      return acc;
    }, new Map());
  }, [orders]);

  const departmentCounters = useMemo(() => groupOrdersByDepartment(orders), [orders]);

  const contextValue = useMemo<ExpressOrdersContextValue>(
    () => ({
      orders,
      isLoading,
      ordersByDepartment,
      departmentCounters,
    }),
    [orders, isLoading, ordersByDepartment, departmentCounters]
  );

  return (
    <ExpressOrdersContext.Provider value={contextValue}>{children}</ExpressOrdersContext.Provider>
  );
};

export const useExpressOrders = (): ExpressOrdersContextValue => {
  const context = useContext(ExpressOrdersContext);
  if (!context) {
    throw new Error('useExpressOrders must be used within ExpressOrdersProvider');
  }
  return context;
};
